import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, Send, Star, User, BarChart3, X, Gift, Lock, ArrowRight, ChevronRight } from 'lucide-react';
import { PrimaryButton } from '@/components/ui';
import { FluidBackground, OverflowWordmark } from '@/components/Brand';
import { AvatarPair } from '@/components/datenite/parts';
import { AdminModal } from '@/components/AdminModal';
import { DateNiteView } from '@/views/DateNiteView';
import { CreatorView } from '@/views/CreatorView';
import { ReviewerView } from '@/views/ReviewerView';
import { RecommenderView } from '@/views/RecommenderView';
import { EmbedTestView } from '@/views/EmbedTestView';
import { DEFAULT_CLIPS, COLORS } from '@/data';
import { d1List, d1Get, d1Upsert, d1DeleteWhere, d1Sync, d1AppendOnly, d1BatchUpsert } from '@/lib/d1-storage';
import { recordLoginEvent } from '@/lib/admin-api';
import { generateCoupleCode } from '@/lib/utils';
import type { Clip, Creator, Reviewer, CoupleData, SubmittedClip, AppMode, CoupleRecommendation, ClipView, WishlistItem, ReviewerFollowUp, CoupleFollowUp, CoupleQuestion, TagTutorial, UserPresence, SuggestedEdit, ClipSuggestionRequest, PickForUsRequest, ClipReview, SubmittedClipReview, TriedClip, ClipSuggestionItem, PickForUsItem } from '@/types';

// D1 returns FK fields that aren't in the original frontend types
type D1ClipReview = ClipReview & { clipId: string };
type D1SubmittedClipReview = SubmittedClipReview & { submittedClipId: string };
type D1ClipSuggestionItem = ClipSuggestionItem & { requestId: string };
type D1PickForUsItem = PickForUsItem & { requestId: string };

// Site access PIN - change this to your desired PIN
const SITE_PIN = '6969';
const PIN_STORAGE_KEY = 'vb-site-access';

// Password Gate Component
const PasswordGate: React.FC<{ onUnlock: () => void }> = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === SITE_PIN) {
      localStorage.setItem(PIN_STORAGE_KEY, 'unlocked');
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
    }
  };

  return (
    <div className="min-h-screen bg-ground text-ink flex items-center justify-center p-6">
      <FluidBackground />
      <div className={`relative z-10 w-full max-w-sm ${shake ? 'animate-shake' : ''}`}>
        <div className="flex flex-col items-center text-center mb-8">
          <OverflowWordmark />
          <p className="text-ink-2 text-[15px] mt-3">Made for the messy ones.</p>
          <p className="text-ink-muted text-[13px] mt-1">Private — enter the PIN to come in.</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-surface border border-line rounded-[24px] p-5">
          <label className="sr-only" htmlFor="site-pin">PIN</label>
          <input
            id="site-pin"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            value={pin}
            onChange={e => { setPin(e.target.value); setError(false); }}
            placeholder="••••"
            className={`w-full h-16 bg-raised border ${error ? 'border-red-400' : 'border-line'} rounded-2xl px-4 text-ink text-center text-3xl tracking-[0.5em] mb-3 focus:outline-none focus:border-action placeholder:text-ink-muted`}
            autoFocus
          />
          {error && <p className="text-red-300 text-sm text-center mb-3" role="alert">That PIN isn’t right</p>}
          <button type="submit" className="w-full h-14 rounded-2xl bg-action text-ground text-[17px] font-bold flex items-center justify-center gap-2 hover:brightness-105">
            <Lock className="w-4 h-4" /> Enter
          </button>
        </form>
        <p className="text-center text-[13px] text-ink-muted mt-5">Got a personal link? Open it instead — it skips this step.</p>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
      `}</style>
    </div>
  );
};

export default function App() {
  // Site access state
  const [siteUnlocked, setSiteUnlocked] = useState(() => {
    return localStorage.getItem(PIN_STORAGE_KEY) === 'unlocked';
  });
  const [mode, setMode] = useState<AppMode>(null);
  const [loading, setLoading] = useState(true);
  const [showReturningLogin, setShowReturningLogin] = useState(false);
  const [returningCode, setReturningCode] = useState('');
  const [returningError, setReturningError] = useState('');
  const [loggedInReviewer, setLoggedInReviewer] = useState<Reviewer | null>(null);

  const [clips, setClips] = useState<SubmittedClip[]>([]);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [dateNiteClips, setDateNiteClips] = useState<Clip[]>([]);
  const [coupleData, setCoupleData] = useState<CoupleData | null>(null);
  const [recommendations, setRecommendations] = useState<CoupleRecommendation[]>([]);
  const [clipViews, setClipViews] = useState<ClipView[]>([]);
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [reviewerFollowUps, setReviewerFollowUps] = useState<ReviewerFollowUp[]>([]);
  const [coupleFollowUps, setCoupleFollowUps] = useState<CoupleFollowUp[]>([]);
  const [coupleQuestions, setCoupleQuestions] = useState<CoupleQuestion[]>([]);
  const [tagTutorials, setTagTutorials] = useState<TagTutorial[]>([]);
  const [suggestedEdits, setSuggestedEdits] = useState<SuggestedEdit[]>([]);
  const [clipSuggestions, setClipSuggestions] = useState<ClipSuggestionRequest[]>([]);
  const [pickForUsRequests, setPickForUsRequests] = useState<PickForUsRequest[]>([]);

  const [adminAuth, setAdminAuth] = useState(false);

  // Refs for diff-based D1 sync (track previous state to compute changes)
  const clipsRef = useRef<SubmittedClip[]>([]);
  const reviewersRef = useRef<Reviewer[]>([]);
  const creatorsRef = useRef<Creator[]>([]);
  const dateNiteClipsRef = useRef<Clip[]>([]);
  const clipViewsRef = useRef<ClipView[]>([]);
  const reviewerFollowUpsRef = useRef<ReviewerFollowUp[]>([]);
  const coupleFollowUpsRef = useRef<CoupleFollowUp[]>([]);
  const coupleQuestionsRef = useRef<CoupleQuestion[]>([]);
  const tagTutorialsRef = useRef<TagTutorial[]>([]);
  const suggestedEditsRef = useRef<SuggestedEdit[]>([]);
  const clipSuggestionsRef = useRef<ClipSuggestionRequest[]>([]);
  const pickForUsRequestsRef = useRef<PickForUsRequest[]>([]);
  const recommendationsRef = useRef<CoupleRecommendation[]>([]);
  const wishlistRef = useRef<WishlistItem[]>([]);

  // Presence tracking - update user online status
  const updatePresence = async (currentPage: AppMode | 'home' | 'admin') => {
    try {
      // Determine user info
      let presence: UserPresence | null = null;

      if (loggedInReviewer) {
        presence = {
          id: `reviewer-${loggedInReviewer.id}`,
          userType: 'reviewer',
          userId: loggedInReviewer.id,
          userName: loggedInReviewer.code,
          currentPage,
          lastSeen: new Date().toISOString(),
        };
      } else if (coupleData?.coupleId) {
        presence = {
          id: `couple-${coupleData.coupleId}`,
          userType: 'couple',
          userId: coupleData.coupleId,
          userName: `${coupleData.hisName} & ${coupleData.herName}`,
          currentPage,
          lastSeen: new Date().toISOString(),
        };
      }

      if (presence) {
        await d1Upsert('user_presence', presence);
      }
    } catch (err) {
      console.warn('Failed to update presence:', err);
    }
  };

  // Update presence on mode change and set up heartbeat
  useEffect(() => {
    const currentPage = mode || 'home';
    updatePresence(currentPage);

    // Heartbeat every 30 seconds
    const heartbeat = setInterval(() => {
      updatePresence(currentPage);
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [mode, loggedInReviewer?.id, coupleData?.coupleId]);

  // Record session starts for the admin People overview (last login / login counts)
  useEffect(() => {
    if (loggedInReviewer?.id && loggedInReviewer.id !== 'Reviewer-TEST') {
      recordLoginEvent('reviewer', loggedInReviewer.id, loggedInReviewer.code);
    }
  }, [loggedInReviewer?.id]);

  useEffect(() => {
    const inDateNite = mode === 'datenite' || window.location.pathname.startsWith('/clip/');
    if (inDateNite && coupleData?.coupleId) {
      recordLoginEvent('couple', coupleData.coupleId, `${coupleData.hisName} & ${coupleData.herName}`);
    }
  }, [mode, coupleData?.coupleId]);

  // Personal links: /c/<couple code> and /r/<reviewer code> log straight in
  const personalLink = window.location.pathname.match(/^\/(c|r)\/([A-Za-z0-9-]+)\/?$/);
  const pendingReviewerCode = useRef<string | null>(personalLink?.[1] === 'r' ? personalLink[2]!.toUpperCase() : null);

  useEffect(() => {
    (async () => {
      if (personalLink?.[1] === 'c') {
        const code = personalLink[2]!.toUpperCase();
        const couple = code.startsWith('VBA-')
          ? (await d1List<CoupleData>('couples', { accessCode: code }).catch(() => []))[0]
          : await d1Get<CoupleData>('couples', code).catch(() => null);
        if (couple?.coupleId) {
          localStorage.setItem('vb-my-couple-id', couple.coupleId);
          localStorage.setItem(PIN_STORAGE_KEY, 'unlocked');
          setSiteUnlocked(true);
          setMode('datenite');
        }
      }
      await loadData();
    })();
  }, []);

  // Reviewer personal link: log in once reviewers have loaded
  useEffect(() => {
    const code = pendingReviewerCode.current;
    if (!code || reviewers.length === 0) return;
    pendingReviewerCode.current = null;
    const found = reviewers.find(r => r.code?.toUpperCase() === code);
    if (found) {
      localStorage.setItem(PIN_STORAGE_KEY, 'unlocked');
      setSiteUnlocked(true);
      setLoggedInReviewer(found);
      setMode('reviewer');
    }
  }, [reviewers]);

  // Reload recommendations when entering datenite mode (to catch new ones sent while away)
  useEffect(() => {
    const loadRecommendations = async () => {
      if (mode === 'datenite' && coupleData?.coupleId) {
        const recs = await d1List<CoupleRecommendation>('couple_recommendations', { coupleId: coupleData.coupleId });
        setRecommendations(recs);
        recommendationsRef.current = recs;
      }
    };
    loadRecommendations();
  }, [mode, coupleData?.coupleId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all global tables from D1 in parallel
      const [
        loadedClips, loadedReviewers, loadedCreators,
        loadedDateNiteClips, loadedClipReviews, loadedSubmittedClipReviews,
        loadedClipViews, loadedFollowUps, loadedCoupleFollowUps,
        loadedCoupleQuestions, loadedTagTutorials, loadedSuggestedEdits,
        loadedClipSuggestions, loadedClipSuggestionItems,
        loadedPickForUs, loadedPickForUsItems,
      ] = await Promise.all([
        d1List<SubmittedClip>('submitted_clips'),
        d1List<Reviewer>('reviewers'),
        d1List<Creator>('creators'),
        d1List<Clip>('clips'),
        d1List<D1ClipReview>('clip_reviews'),
        d1List<D1SubmittedClipReview>('submitted_clip_reviews'),
        d1List<ClipView>('clip_views'),
        d1List<ReviewerFollowUp>('reviewer_followups'),
        d1List<CoupleFollowUp>('couple_followups'),
        d1List<CoupleQuestion>('couple_questions'),
        d1List<TagTutorial>('tag_tutorials'),
        d1List<SuggestedEdit>('suggested_edits'),
        d1List<ClipSuggestionRequest>('clip_suggestion_requests'),
        d1List<D1ClipSuggestionItem>('clip_suggestion_items'),
        d1List<PickForUsRequest>('pick_for_us_requests'),
        d1List<D1PickForUsItem>('pick_for_us_items'),
      ]);

      // Reassemble nested children into parent objects
      // clips + clip_reviews → clip.reviews[]
      const reviewsByClip = new Map<string, ClipReview[]>();
      for (const r of loadedClipReviews) reviewsByClip.set(r.clipId, [...(reviewsByClip.get(r.clipId) || []), r]);
      const assembledDateNiteClips = loadedDateNiteClips.map(c => ({
        ...c,
        reviews: reviewsByClip.get(c.id) || [],
      }));

      // Merge with DEFAULT_CLIPS (seed if needed)
      let finalDateNiteClips: Clip[];
      if (assembledDateNiteClips.length > 0) {
        const merged = DEFAULT_CLIPS.map(def => {
          const existing = assembledDateNiteClips.find(s => s.id === def.id);
          return existing ? { ...def, ...existing, tags: existing.tags || def.tags } : def;
        });
        const newClips = assembledDateNiteClips.filter(s => !DEFAULT_CLIPS.find(d => d.id === s.id));
        finalDateNiteClips = [...merged, ...newClips];
      } else {
        finalDateNiteClips = DEFAULT_CLIPS;
        // Seed DEFAULT_CLIPS into D1
        await d1BatchUpsert('clips', DEFAULT_CLIPS.map(({ reviews, ...rest }) => rest) as Record<string, unknown>[]);
      }

      // submitted_clips + submitted_clip_reviews → clip.reviews[]
      const subReviewsByClip = new Map<string, SubmittedClipReview[]>();
      for (const r of loadedSubmittedClipReviews) {
        subReviewsByClip.set(r.submittedClipId, [...(subReviewsByClip.get(r.submittedClipId) || []), r]);
      }
      const assembledClips = loadedClips.map(c => ({
        ...c,
        reviews: subReviewsByClip.get(c.id) || [],
      }));

      // clip_suggestion_requests + items → request.clips[]
      const sugItemsByReq = new Map<string, ClipSuggestionItem[]>();
      for (const item of loadedClipSuggestionItems) {
        sugItemsByReq.set(item.requestId, [...(sugItemsByReq.get(item.requestId) || []), item]);
      }
      const assembledSuggestions = loadedClipSuggestions.map(r => ({
        ...r,
        clips: sugItemsByReq.get(r.id) || [],
      }));

      // pick_for_us_requests + items → request.items[]
      const pickItemsByReq = new Map<string, PickForUsItem[]>();
      for (const item of loadedPickForUsItems) {
        pickItemsByReq.set(item.requestId, [...(pickItemsByReq.get(item.requestId) || []), item]);
      }
      const assembledPickForUs = loadedPickForUs.map(r => ({
        ...r,
        items: pickItemsByReq.get(r.id) || [],
      }));

      // Set state
      setClips(assembledClips);           clipsRef.current = assembledClips;
      setReviewers(loadedReviewers);      reviewersRef.current = loadedReviewers;
      setCreators(loadedCreators);        creatorsRef.current = loadedCreators;
      setDateNiteClips(finalDateNiteClips); dateNiteClipsRef.current = finalDateNiteClips;
      setClipViews(loadedClipViews);      clipViewsRef.current = loadedClipViews;
      setReviewerFollowUps(loadedFollowUps); reviewerFollowUpsRef.current = loadedFollowUps;
      setCoupleFollowUps(loadedCoupleFollowUps); coupleFollowUpsRef.current = loadedCoupleFollowUps;
      setCoupleQuestions(loadedCoupleQuestions); coupleQuestionsRef.current = loadedCoupleQuestions;
      setTagTutorials(loadedTagTutorials); tagTutorialsRef.current = loadedTagTutorials;
      setSuggestedEdits(loadedSuggestedEdits); suggestedEditsRef.current = loadedSuggestedEdits;
      setClipSuggestions(assembledSuggestions); clipSuggestionsRef.current = assembledSuggestions;
      setPickForUsRequests(assembledPickForUs); pickForUsRequestsRef.current = assembledPickForUs;

      // Load couple data from localStorage ID
      let parsedCoupleData: CoupleData | null = null;
      const localCoupleId = localStorage.getItem('vb-my-couple-id');
      if (localCoupleId) {
        const couple = await d1Get<CoupleData>('couples', localCoupleId);
        if (couple) {
          const tried = await d1List<TriedClip>('tried_clips', { coupleId: localCoupleId });
          parsedCoupleData = { ...couple, tried: tried || [] };
        }
      }

      setCoupleData(parsedCoupleData);
      coupleDataRef.current = parsedCoupleData;

      // Load couple-specific data
      if (parsedCoupleData?.coupleId) {
        const [recs, wish] = await Promise.all([
          d1List<CoupleRecommendation>('couple_recommendations', { coupleId: parsedCoupleData.coupleId }),
          d1List<WishlistItem>('wishlist_items', { coupleId: parsedCoupleData.coupleId }),
        ]);
        setRecommendations(recs);    recommendationsRef.current = recs;
        setWishlist(wish);           wishlistRef.current = wish;
      }
    } catch (err) {
      console.log('D1 load error:', err);
    }
    setLoading(false);
  };

  // ---- D1 diff-based save functions ----
  // Same signatures as before (accept full arrays) but internally diff
  // against previous state and apply row-level D1 changes.
  // This means ZERO changes needed at the 60+ call sites.

  const coupleDataRef = useRef<CoupleData | null>(null);

  const saveCoupleData = async (data: CoupleData) => {
    if (!data.coupleId) {
      data = { ...data, coupleId: generateCoupleCode() };
    }
    // Capture prev BEFORE updating state/ref
    const prevTried = coupleDataRef.current?.tried || [];
    localStorage.setItem('vb-my-couple-id', data.coupleId);
    setCoupleData(data);
    coupleDataRef.current = data;

    // Separate tried[] from couple flat fields
    const { tried, ...coupleFields } = data;
    await d1Upsert('couples', coupleFields as Record<string, unknown>);

    // Sync tried clips — only if actually changed
    const nextTried = tried || [];
    if (JSON.stringify(prevTried) !== JSON.stringify(nextTried)) {
      await d1DeleteWhere('tried_clips', { coupleId: data.coupleId });
      if (nextTried.length > 0) {
        await d1BatchUpsert('tried_clips', nextTried.map(t => ({ ...t, coupleId: data.coupleId })));
      }
    }
  };

  const saveRecommendations = async (recs: CoupleRecommendation[]) => {
    if (!coupleData?.coupleId) return;
    const prev = recommendationsRef.current;
    setRecommendations(recs);
    recommendationsRef.current = recs;
    try { await d1Sync('couple_recommendations', prev, recs, 'id'); }
    catch (err) { console.error('D1 sync recommendations failed:', err); }
  };

  const saveClipViews = async (views: ClipView[]) => {
    const prev = clipViewsRef.current;
    setClipViews(views);
    clipViewsRef.current = views;
    try { await d1AppendOnly('clip_views', prev, views); }
    catch (err) { console.error('D1 sync clip views failed:', err); }
  };

  const saveWishlist = async (items: WishlistItem[], _coupleId?: string) => {
    const prev = wishlistRef.current;
    setWishlist(items);
    wishlistRef.current = items;
    try { await d1Sync('wishlist_items', prev, items, 'id'); }
    catch (err) { console.error('D1 sync wishlist failed:', err); }
  };

  const saveReviewerFollowUps = async (followUps: ReviewerFollowUp[]) => {
    const prev = reviewerFollowUpsRef.current;
    setReviewerFollowUps(followUps);
    reviewerFollowUpsRef.current = followUps;
    try { await d1Sync('reviewer_followups', prev, followUps, 'id'); }
    catch (err) {
      console.error('D1 sync reviewer follow-ups failed:', err);
      alert('Save may not have synced. Your change is saved locally.');
    }
  };

  const saveCoupleFollowUps = async (followUps: CoupleFollowUp[]) => {
    const prev = coupleFollowUpsRef.current;
    setCoupleFollowUps(followUps);
    coupleFollowUpsRef.current = followUps;
    try { await d1Sync('couple_followups', prev, followUps, 'id'); }
    catch (err) {
      console.error('D1 sync couple follow-ups failed:', err);
      alert('Save may not have synced. Your change is saved locally.');
    }
  };

  const saveCoupleQuestions = async (questions: CoupleQuestion[]) => {
    const prev = coupleQuestionsRef.current;
    setCoupleQuestions(questions);
    coupleQuestionsRef.current = questions;
    try { await d1Sync('couple_questions', prev, questions, 'id'); }
    catch (err) {
      console.error('D1 sync couple questions failed:', err);
      alert('Save may not have synced. Your change is saved locally.');
    }
  };

  const saveTagTutorials = async (tutorials: TagTutorial[]) => {
    const prev = tagTutorialsRef.current;
    setTagTutorials(tutorials);
    tagTutorialsRef.current = tutorials;
    try { await d1Sync('tag_tutorials', prev, tutorials, 'id'); }
    catch (err) { console.error('D1 sync tag tutorials failed:', err); }
  };

  const saveSuggestedEdits = async (edits: SuggestedEdit[]) => {
    const prev = suggestedEditsRef.current;
    setSuggestedEdits(edits);
    suggestedEditsRef.current = edits;
    try { await d1Sync('suggested_edits', prev, edits, 'id'); }
    catch (err) { console.error('D1 sync suggested edits failed:', err); }
  };

  const saveClipSuggestions = async (suggestions: ClipSuggestionRequest[]) => {
    const prev = clipSuggestionsRef.current;
    setClipSuggestions(suggestions);
    clipSuggestionsRef.current = suggestions;
    try {
      // Sync parent rows only, then sync children for changed parents
      const prevParents = prev.map(({ clips, ...rest }) => rest);
      const nextParents = suggestions.map(({ clips, ...rest }) => rest);
      await d1Sync('clip_suggestion_requests', prevParents, nextParents, 'id');
      // Sync children for any changed suggestion
      for (const sug of suggestions) {
        const prevSug = prev.find(p => p.id === sug.id);
        if (!prevSug || JSON.stringify(prevSug.clips) !== JSON.stringify(sug.clips)) {
          await d1DeleteWhere('clip_suggestion_items', { requestId: sug.id });
          if (sug.clips?.length) await d1BatchUpsert('clip_suggestion_items', sug.clips.map(c => ({ ...c, requestId: sug.id })));
        }
      }
    }
    catch (err) { console.error('D1 sync clip suggestions failed:', err); }
  };

  const savePickForUsRequests = async (requests: PickForUsRequest[]) => {
    const prev = pickForUsRequestsRef.current;
    setPickForUsRequests(requests);
    pickForUsRequestsRef.current = requests;
    try {
      const prevParents = prev.map(({ items, ...rest }) => rest);
      const nextParents = requests.map(({ items, ...rest }) => rest);
      await d1Sync('pick_for_us_requests', prevParents, nextParents, 'id');
      for (const req of requests) {
        const prevReq = prev.find(p => p.id === req.id);
        if (!prevReq || JSON.stringify(prevReq.items) !== JSON.stringify(req.items)) {
          await d1DeleteWhere('pick_for_us_items', { requestId: req.id });
          if (req.items?.length) await d1BatchUpsert('pick_for_us_items', req.items.map(i => ({ ...i, requestId: req.id })));
        }
      }
    }
    catch (err) { console.error('D1 sync pick for us failed:', err); }
  };

  // Named save functions for entities previously using generic saveData()
  const saveReviewers = async (data: Reviewer[]) => {
    const prev = reviewersRef.current;
    setReviewers(data);
    reviewersRef.current = data;
    try { await d1Sync('reviewers', prev, data, 'id'); }
    catch (err) { console.error('D1 sync reviewers failed:', err); }
  };

  const saveCreators = async (data: Creator[]) => {
    const prev = creatorsRef.current;
    setCreators(data);
    creatorsRef.current = data;
    try { await d1Sync('creators', prev, data, 'id'); }
    catch (err) { console.error('D1 sync creators failed:', err); }
  };

  const saveSubmittedClips = async (data: SubmittedClip[]) => {
    const prev = clipsRef.current;
    setClips(data);
    clipsRef.current = data;
    try {
      // Sync parent rows only. reviews[] lives in submitted_clip_reviews; the legacy
      // `review` column is kept so INSERT OR REPLACE doesn't blank it.
      const prevParents = prev.map(({ reviews, ...rest }) => rest);
      const nextParents = data.map(({ reviews, ...rest }) => rest);
      await d1Sync('submitted_clips', prevParents, nextParents, 'id');
      // Reviews are saved directly per-reviewer in handleSaveSubmissionReview — NOT here
    } catch (err) { console.error('D1 sync submitted clips failed:', err); }
  };

  const saveDateNiteClips = async (data: Clip[]) => {
    const prev = dateNiteClipsRef.current;
    setDateNiteClips(data);
    dateNiteClipsRef.current = data;
    try {
      // Sync parent rows only (strip reviews to avoid wiping child table)
      const prevParents = prev.map(({ reviews, ...rest }) => rest);
      const nextParents = data.map(({ reviews, ...rest }) => rest);
      await d1Sync('clips', prevParents, nextParents, 'id');
      // Reviews are managed separately — clip_reviews synced via d1Upsert per review
    } catch (err) { console.error('D1 sync datenite clips failed:', err); }
  };

  const loginReturningReviewer = () => {
    const code = String(returningCode).replace(/\s/g, '').toUpperCase();
    if (code === 'TEST' || code === 'VBR-TEST') {
      setLoggedInReviewer({
        id: 'Reviewer-TEST',
        code: 'TEST',
        gender: 'Other',
        ageRange: '25-34',
        watchesContent: 'Regularly',
        registeredAt: '2024-01-01',
        relationshipStatus: 'In a relationship',
        watchesAdultContent: 'Yes regularly',
        familiarWithProducts: 'Yes',
        enjoysVolumeContent: 'Yes'
      });
      setShowReturningLogin(false);
      setReturningCode('');
      setReturningError('');
      setMode('reviewer');
      return;
    }
    const found = reviewers.find(r => r.code && r.code.toUpperCase() === code);
    if (found) {
      setLoggedInReviewer(found);
      setShowReturningLogin(false);
      setReturningCode('');
      setReturningError('');
      setMode('reviewer');
    } else {
      setReturningError(`Code not found: "${code}"`);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Shared clip links skip the PIN; personal links unlock the site once their code checks out
  const pinFree = location.pathname.startsWith('/clip/') || (!!personalLink && loading);
  if (!siteUnlocked && !pinFree) {
    return <PasswordGate onUnlock={() => setSiteUnlocked(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: COLORS.lime }} />
      </div>
    );
  }

  // Render embed test page
  if (location.pathname === '/embed-test') {
    return (
      <Routes>
        <Route path="/embed-test" element={<EmbedTestView />} />
      </Routes>
    );
  }

  // Render clip in Date Night view if on /clip/:clipId route
  if (location.pathname.startsWith('/clip/')) {
    const clipId = location.pathname.replace('/clip/', '');
    return (
      <DateNiteView
        clips={dateNiteClips}
        saveClips={saveDateNiteClips}
        coupleData={coupleData}
        saveCoupleData={saveCoupleData}
        recommendations={recommendations}
        saveRecommendations={saveRecommendations}
        onBack={() => navigate('/')}
        initialClipId={clipId}
        clipViews={clipViews}
        saveClipViews={saveClipViews}
        wishlist={wishlist}
        saveWishlist={saveWishlist}
        coupleFollowUps={coupleFollowUps}
        saveCoupleFollowUps={saveCoupleFollowUps}
        coupleQuestions={coupleQuestions}
        saveCoupleQuestions={saveCoupleQuestions}
        clipSuggestions={clipSuggestions}
        saveClipSuggestions={saveClipSuggestions}
        pickForUsRequests={pickForUsRequests}
        savePickForUsRequests={savePickForUsRequests}
      />
    );
  }

  if (mode === 'datenite') {
    return (
      <DateNiteView
        clips={dateNiteClips}
        saveClips={saveDateNiteClips}
        coupleData={coupleData}
        saveCoupleData={saveCoupleData}
        recommendations={recommendations}
        saveRecommendations={saveRecommendations}
        onBack={() => setMode(null)}
        clipViews={clipViews}
        saveClipViews={saveClipViews}
        wishlist={wishlist}
        saveWishlist={saveWishlist}
        coupleFollowUps={coupleFollowUps}
        saveCoupleFollowUps={saveCoupleFollowUps}
        coupleQuestions={coupleQuestions}
        saveCoupleQuestions={saveCoupleQuestions}
        clipSuggestions={clipSuggestions}
        saveClipSuggestions={saveClipSuggestions}
        pickForUsRequests={pickForUsRequests}
        savePickForUsRequests={savePickForUsRequests}
      />
    );
  }

  if (mode === 'creator') {
    return (
      <CreatorView
        creators={creators}
        saveCreators={saveCreators}
        clips={clips}
        saveClips={saveSubmittedClips}
        onBack={() => setMode(null)}
      />
    );
  }

  if (mode === 'reviewer') {
    return (
      <ReviewerView
        reviewers={reviewers}
        saveReviewers={saveReviewers}
        dateNiteClips={dateNiteClips}
        saveDateNiteClips={saveDateNiteClips}
        currentReviewer={loggedInReviewer}
        setCurrentReviewer={setLoggedInReviewer}
        onBack={() => { setMode(null); setLoggedInReviewer(null); }}
        loadData={loadData}
        submittedClips={clips}
        saveSubmittedClips={saveSubmittedClips}
        creators={creators}
        clipViews={clipViews}
        saveClipViews={saveClipViews}
        wishlist={wishlist}
        saveWishlist={saveWishlist}
        coupleQuestions={coupleQuestions}
        saveCoupleQuestions={saveCoupleQuestions}
        tagTutorials={tagTutorials}
        saveTagTutorials={saveTagTutorials}
        reviewerFollowUps={reviewerFollowUps}
        saveReviewerFollowUps={saveReviewerFollowUps}
        suggestedEdits={suggestedEdits}
        saveSuggestedEdits={saveSuggestedEdits}
        coupleFollowUps={coupleFollowUps}
        saveCoupleFollowUps={saveCoupleFollowUps}
        pickForUsRequests={pickForUsRequests}
        savePickForUsRequests={savePickForUsRequests}
        clipSuggestions={clipSuggestions}
        saveClipSuggestions={saveClipSuggestions}
        onSwitchToCoupleMode={async (coupleId: string) => {
          // Load couple data from D1 and switch to DateNite view
          const couple = await d1Get<CoupleData>('couples', coupleId);
          if (couple) {
            const tried = await d1List<TriedClip>('tried_clips', { coupleId });
            const loadedCoupleData = { ...couple, tried: tried || [] };
            setCoupleData(loadedCoupleData);
            coupleDataRef.current = loadedCoupleData;
            localStorage.setItem('vb-my-couple-id', coupleId);
            const recs = await d1List<CoupleRecommendation>('couple_recommendations', { coupleId });
            setRecommendations(recs); recommendationsRef.current = recs;
            const wish = await d1List<WishlistItem>('wishlist_items', { coupleId });
            setWishlist(wish); wishlistRef.current = wish;
            setMode('datenite');
          }
        }}
      />
    );
  }

  if (mode === 'recommend') {
    return (
      <RecommenderView
        dateNiteClips={dateNiteClips}
        onBack={() => setMode(null)}
      />
    );
  }

  if (mode === 'admin') {
    return (
      <AdminModal
        clips={clips}
        creators={creators}
        reviewers={reviewers}
        dateNiteClips={dateNiteClips}
        saveDateNiteClips={saveDateNiteClips}
        auth={adminAuth}
        setAuth={setAdminAuth}
        onClose={() => { setMode(null); setAdminAuth(false); }}
        clipViews={clipViews}
        wishlist={wishlist}
        reviewerFollowUps={reviewerFollowUps}
        saveReviewerFollowUps={saveReviewerFollowUps}
        coupleFollowUps={coupleFollowUps}
        saveCoupleFollowUps={saveCoupleFollowUps}
        coupleQuestions={coupleQuestions}
        saveCoupleQuestions={saveCoupleQuestions}
        tagTutorials={tagTutorials}
        saveTagTutorials={saveTagTutorials}
        suggestedEdits={suggestedEdits}
        saveSuggestedEdits={saveSuggestedEdits}
        clipSuggestions={clipSuggestions}
        saveClipSuggestions={saveClipSuggestions}
        onRefresh={loadData}
        saveClips={saveSubmittedClips}
        saveReviewers={saveReviewers}
      />
    );
  }

  const homeRow = (icon: React.ReactNode, title: string, desc: string, onClick: () => void) => (
    <button onClick={onClick} className="w-full flex items-center gap-3.5 p-3.5 rounded-[18px] bg-surface/55 backdrop-blur-md border border-line hover:border-line-strong text-left group">
      <span className="w-11 h-11 rounded-xl bg-raised text-ink-2 flex items-center justify-center shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[16px] font-semibold text-ink">{title}</span>
        <span className="block text-[13px] text-ink-muted">{desc}</span>
      </span>
      <ChevronRight className="w-5 h-5 text-ink-muted group-hover:text-ink group-hover:translate-x-0.5 transition-transform" />
    </button>
  );

  return (
    <div className="min-h-screen bg-ground text-ink flex justify-center lg:items-center px-5 lg:px-10 pt-[max(3rem,env(safe-area-inset-top))] pb-10">
      <FluidBackground />
      <div className="relative z-10 max-w-md lg:max-w-5xl w-full lg:grid lg:grid-cols-[1.15fr_1fr] lg:gap-14 lg:items-center">
        <div>
        <header className="mb-8 lg:mb-10">
          <OverflowWordmark />
          <p className="mt-4 lg:mt-6 font-display text-[24px] lg:text-[30px] leading-tight max-w-md">
            Made for the <span className="italic text-her">messy</span> ones.
          </p>
          <p className="text-ink-2 text-[15px] lg:text-[17px] mt-2 leading-relaxed max-w-md">Bigger finishes, wetter nights. Clips, date-night ideas and honest reviews for couples who love it when it spills over.</p>
        </header>

        <button
          onClick={() => setMode('datenite')}
          className="relative w-full overflow-hidden rounded-[24px] border border-her/30 bg-gradient-to-br from-her-tint/80 via-surface/70 to-him-tint/80 backdrop-blur-md p-5 lg:p-8 text-left hover:border-her/60 group"
        >
          <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-her">Date Night</span>
          {coupleData ? (
            <>
              <span className="mt-3 flex items-center gap-3">
                <AvatarPair hisName={coupleData.hisName} herName={coupleData.herName} size="lg" />
                <span className="font-display text-[28px] leading-tight font-medium">{coupleData.hisName} & {coupleData.herName}</span>
              </span>
              <span className="mt-4 inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-action text-ground text-[16px] font-bold">
                Open tonight <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </>
          ) : (
            <>
              <span className="block mt-2 font-display text-[28px] leading-tight font-medium">Plan tonight together</span>
              <span className="block mt-1 text-[14px] text-ink-2">Find the messiest clips, spin for tonight’s finish, plan the week and keep track of what you’ve tried.</span>
              <span className="mt-4 inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-action text-ground text-[16px] font-bold">
                Start or log in <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </>
          )}
        </button>
        </div>

        <div>
        <section data-squirt-target className="mt-7 lg:mt-0 space-y-2.5">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.12em] text-ink-muted">Reviewers</h2>
          <div className="grid grid-cols-2 gap-2.5">
            <button onClick={() => setShowReturningLogin(true)} className="h-[84px] rounded-[18px] bg-surface/55 backdrop-blur-md border border-line hover:border-line-strong p-3.5 text-left">
              <User className="w-5 h-5 text-action" />
              <span className="block mt-2 text-[15px] font-semibold">I have a code</span>
            </button>
            <button onClick={() => setMode('reviewer')} className="h-[84px] rounded-[18px] bg-surface/55 backdrop-blur-md border border-line hover:border-line-strong p-3.5 text-left">
              <Star className="w-5 h-5 text-attention" />
              <span className="block mt-2 text-[15px] font-semibold">Become a reviewer</span>
            </button>
          </div>
        </section>

        <section className="mt-7 space-y-2.5">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.12em] text-ink-muted">More</h2>
          {homeRow(<Send className="w-5 h-5" />, 'Submit content', 'Creators: send us a clip', () => setMode('creator'))}
          {homeRow(<Gift className="w-5 h-5" />, 'Recommend a clip', 'For a friend’s date night', () => setMode('recommend'))}
        </section>

        <button onClick={() => setMode('admin')} className="mt-8 mx-auto flex items-center gap-1.5 h-10 px-3 text-[13px] font-semibold text-ink-muted hover:text-ink">
          <BarChart3 className="w-4 h-4" /> Admin
        </button>
        </div>
      </div>

      {showReturningLogin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-[24px] p-6 w-full max-w-md border border-line text-ink">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-medium">Welcome back</h2>
              <button
                onClick={() => { setShowReturningLogin(false); setReturningCode(''); setReturningError(''); }}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <input
              type="text"
              value={returningCode}
              onChange={e => { setReturningCode(e.target.value.toUpperCase()); setReturningError(''); }}
              onKeyDown={e => e.key === 'Enter' && loginReturningReviewer()}
              placeholder="VBR-XXXXXX"
              className="w-full h-14 bg-raised border border-line rounded-2xl px-4 text-ink font-mono text-center text-lg tracking-wider mb-4 focus:outline-none focus:border-action"
              autoFocus
            />
            {returningError && <p className="text-red-400 text-sm mb-4">{returningError}</p>}
            <PrimaryButton onClick={loginReturningReviewer} className="w-full">
              Access Dashboard
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}
